import { useMemo, useState } from "react";
import { timelineEvents } from "@/mocks/qxian";

const cats = ["全部", ...Array.from(new Set(timelineEvents.map((e) => e.category)))];

export default function ProgressTimeline() {
  const [cat, setCat] = useState("全部");
  const [activeId, setActiveId] = useState(timelineEvents[0].id);

  const events = useMemo(
    () => (cat === "全部" ? timelineEvents : timelineEvents.filter((e) => e.category === cat)),
    [cat]
  );
  const active = events.find((e) => e.id === activeId) || events[0];

  return (
    <section id="timeline" className="w-full bg-background-50 scroll-mt-16 py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary-600">
            <i className="ri-stackshare-line"></i>
            长线事件 · 分集同步
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-black text-foreground-950">事件进展时间线</h2>
          <p className="mt-2 text-sm text-foreground-600">
            事件再长也不怕：先看进展摘要与关键节点，再按时间逐条展开，几分钟内对齐前因后果。
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => {
                setCat(c);
                const first = c === "全部" ? timelineEvents[0] : timelineEvents.find((e) => e.category === c);
                if (first) setActiveId(first.id);
              }}
              className={
                "whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer " +
                (cat === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-300 text-foreground-700 hover:border-primary-300")
              }
            >
              {c}
            </button>
          ))}
        </div>

        {!active ? (
          <div className="mt-8 rounded-xl bg-background-100 border border-background-200 p-10 text-center text-sm text-foreground-600">
            该分类暂无正在跟进的事件。
          </div>
        ) : (
          <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,380px)_1fr]">
            {/* 事件列表 */}
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible">
              {events.map((e) => {
                const isActive = e.id === active.id;
                return (
                  <button
                    key={e.id}
                    onClick={() => setActiveId(e.id)}
                    className={
                      "shrink-0 lg:w-full text-left rounded-xl border p-4 transition-colors cursor-pointer " +
                      (isActive
                        ? "border-primary-300 bg-primary-50/70"
                        : "border-background-200 bg-background-100/60 hover:border-primary-200")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="rounded-full bg-background-50 px-2.5 py-0.5 text-xs text-foreground-600">{e.category}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-primary-600">
                        <i className="ri-fire-fill"></i>
                        {e.heat}
                      </span>
                    </div>
                    <h3 className={"mt-2 text-sm font-semibold leading-snug " + (isActive ? "text-primary-700" : "text-foreground-900")}>
                      {e.title}
                    </h3>
                    <div className="mt-3 h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${e.progress}%` }}></div>
                    </div>
                    <div className="mt-1 text-[11px] text-foreground-400">进展 {e.progress}%</div>
                  </button>
                );
              })}
            </div>

            {/* 详情时间线 */}
            <div className="rounded-2xl border border-background-200 bg-background-100/50 p-5 md:p-7">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">{active.title}</h3>
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 text-accent-900 px-2.5 py-0.5 text-xs">
                      <i className="ri-leaf-line"></i>
                      跟进中
                    </span>
                  </div>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{active.summary}</p>
                </div>
                <div className="shrink-0 w-40 rounded-xl bg-background-50 border border-background-200 p-3">
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>跟进进度</span>
                    <span className="font-semibold text-primary-600">{active.progress}%</span>
                  </div>
                  <div className="mt-2 h-2 w-full rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${active.progress}%` }}></div>
                  </div>
                  <div className="mt-2 text-[11px] text-foreground-400">{active.updates.length} 个关键节点</div>
                </div>
              </div>

              <ol className="mt-6 relative border-l-2 border-primary-200 pl-6 space-y-6">
                {active.updates.map((u, i) => (
                  <li key={i} className="relative">
                    <span
                      className={
                        "absolute -left-[33px] top-0.5 w-4 h-4 rounded-full ring-4 ring-background-100 " +
                        (i === active.updates.length - 1 ? "bg-primary-500 live-dot" : "bg-background-300")
                      }
                    ></span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-heading text-sm font-bold text-foreground-800">{u.time}</span>
                      <span className="rounded-full bg-secondary-100 text-secondary-900 px-2.5 py-0.5 text-[11px]">{u.tag}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{u.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}